import React, { useRef, useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import queryString from 'querystringify';

import styles from './search.scss'; // @TODO tidy these up when removing non-disco search
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import {
  SearchInput,
  type SearchInputRefs,
} from '../search-input/search-input';
import { notifySearchTerm } from '../navigation/notify-analytics';
import { CloseButton } from '../typeahead-overlay/CloseButton';

const SEARCH_BOX_TIMING_EVENT = 'jl-events.browser.searchBoxTiming';
const SEARCH_BOX_TIMING_ENDPOINT = '/header/api/telemetry/search-box';

interface SearchProps {
  id: string;
  hideSearch?: boolean;
  onFocus?: () => void;
  isTypeaheadOpen?: boolean;
  onCloseButtonClick?: () => void;
  onTabOut?: () => void;
}

const forwardSearchBoxTiming = (detail: {
  endpoint?: string;
  durationMs?: number;
}) => {
  const payload = JSON.stringify({
    endpoint: detail?.endpoint,
    durationMs: detail?.durationMs,
  });

  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function'
  ) {
    navigator.sendBeacon(
      SEARCH_BOX_TIMING_ENDPOINT,
      new Blob([payload], { type: 'application/json' })
    );
    return;
  }

  if (typeof fetch === 'function') {
    fetch(SEARCH_BOX_TIMING_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: true,
    }).catch(() => null);
  }
};

const loadSearchBoxFiles = (_callbackFunction: () => void) => {
  if (typeof window !== 'undefined') {
    const baseJLUrl =
      window.location.host === 'www.johnlewis.com'
        ? 'https://www.johnlewis.com'
        : 'https://www.integration.project4.com';

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `${baseJLUrl}/search-box-ui/static/index.css`;
    const cssLoaded = new Promise((resolve, reject) => {
      link.addEventListener('load', resolve);
      link.addEventListener('error', reject);
    });
    document.head.appendChild(link);

    const script = document.createElement('script');
    const jsLoaded = new Promise((resolve, reject) => {
      script.addEventListener('load', resolve);
      script.addEventListener('error', reject);
    });
    script.type = 'text/javascript';
    script.src = `${baseJLUrl}/search-box-ui/static/index.js`;
    document.head.appendChild(script);

    window.monetateQ = window.monetateQ || [];
    Promise.all([cssLoaded, jsLoaded])
      .then(() => {
        window.monetateQ?.push(['trackEvent', ['typeaheadFilesLoadSuccess']]);
        _callbackFunction();
      })
      .catch(err => {
        window.monetateQ?.push(['trackEvent', ['typeaheadFilesLoadError']]);
        console.error('Error loading basic search files ', err);
      });
  } else {
    _callbackFunction();
  }
};

type FocusableRef = {
  current: {
    focus: () => void;
  } | null;
};

export const submitFormValidation =
  (id: string, box: FocusableRef) => (e: any) => {
    const input = e.target.elements[id] as HTMLInputElement;
    if (input.value === '') {
      e.preventDefault();
      window.scrollTo(0, 0);
      box.current?.focus();
    } else {
      notifySearchTerm(input.value);
    }
  };

export const Search = ({
  id,
  hideSearch = false,
  onFocus = () => {},
  isTypeaheadOpen = false,
  onCloseButtonClick = () => {},
  onTabOut = () => {},
}: SearchProps) => {
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window !== 'undefined') {
      const existingInput = document.getElementById(
        id
      ) as HTMLInputElement | null;
      return existingInput?.value || '';
    }
    return '';
  });
  const hasUserTypedRef = useRef(false);
  const { features } = useContext(
    EnvVarsFeaturesContext
  ) as EnvVarsFeaturesContextValue;

  const parsedQuery: Record<string, string> =
    typeof window === 'object' ? queryString.parse(window.location.search) : {};

  useEffect(function setSearchTermFromUrl() {
    const urlSearchTerm =
      parsedQuery['search-term'] || parsedQuery.originalSearchTerm;

    if (!parsedQuery.listHead && urlSearchTerm && !hasUserTypedRef.current) {
      setSearchTerm(urlSearchTerm);
    }
  }, []);

  useEffect(function forwardSearchBoxTimingEventsToHeaderUiServer() {
    if (typeof document === 'undefined' || !document.body) {
      return undefined;
    }

    const handleSearchBoxTiming = (event: Event) => {
      const { detail } = event as CustomEvent<{
        endpoint?: string;
        durationMs?: number;
      }>;
      forwardSearchBoxTiming(detail);
    };

    document.body.addEventListener(
      SEARCH_BOX_TIMING_EVENT,
      handleSearchBoxTiming
    );

    return () => {
      document.body.removeEventListener(
        SEARCH_BOX_TIMING_EVENT,
        handleSearchBoxTiming
      );
    };
  }, []);

  const results = useRef<HTMLDivElement | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const box = useRef<HTMLInputElement | null>(null);
  const [loaded, setLoaded] = useState<boolean | null>(null);

  const submitForm = submitFormValidation(id, box);

  const resetForm = () => {
    hasUserTypedRef.current = false;
    setSearchTerm('');
    window.scrollTo(0, 0);
    box.current?.focus();
  };

  const typeaheadResultsClasses = classNames(styles.typeaheadResults, {
    [styles.typeaheadResults__hidden]: !isTypeaheadOpen,
  });

  const searchInputRefs: SearchInputRefs = {
    box,
    form,
    results,
    container,
  };

  return (
    <>
      <div
        className={classNames(styles.form, {
          [styles.hide]: hideSearch,
        })}
        data-search-form
        ref={container}
        id="search-container"
      >
        <form
          data-testid="search-form"
          name="search-form"
          action="/search"
          className={classNames(styles.form, {
            [styles.form__expanded]: isTypeaheadOpen,
          })}
          ref={form}
          onSubmit={submitForm}
          onReset={resetForm}
        >
          <label htmlFor={id} className={styles.visuallyHidden}>
            Search
          </label>
          <SearchInput
            id={id}
            searchTerm={searchTerm}
            setSearchTerm={value => {
              hasUserTypedRef.current = true;
              setSearchTerm(value);
            }}
            loaded={loaded}
            loadSearchBoxFiles={loadSearchBoxFiles}
            setLoaded={setLoaded}
            features={features as Features}
            inputRefs={searchInputRefs}
            parentOnFocus={onFocus}
            isTypeaheadOpen={isTypeaheadOpen}
            showClear={Boolean(searchTerm.length)}
          />
        </form>

        <CloseButton
          isTypeaheadOpen={isTypeaheadOpen}
          onCloseButtonClick={onCloseButtonClick}
          isDesktopCloseButton
          onTabOut={onTabOut}
        />
      </div>
      <div
        ref={results}
        className={typeaheadResultsClasses}
        onBlur={onTabOut}
      />
    </>
  );
};
